/**
 * PositionInspector - Guided Positioning Controls for Page Builder Elements
 * 
 * Provides safe positioning controls instead of free drag-and-drop:
 * - Alignment presets (left / center / right)
 * - Vertical placement (top / middle / bottom within section)
 * - Nudge controls (↑ ↓ ← → with increments)
 * - Spacing presets
 * 
 * Architecture prepared for future drag-and-drop with:
 * - Snap zones defined for container bounds, center lines, element edges, grid
 * - Position values stored as { align, vAlign, offsetX, offsetY, spacing }
 */
import React from 'react';
import { 
  AlignLeft, AlignCenter, AlignRight,
  AlignStartVertical, AlignCenterVertical, AlignEndVertical,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
  RotateCcw, Grid3X3, Move
} from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

// Nudge increment options
const NUDGE_INCREMENTS = {
  fine: 1,
  normal: 4,
  coarse: 16,
};

// Spacing presets
const SPACING_PRESETS = {
  none: { value: 0, label: 'None' },
  tight: { value: 4, label: 'Tight (4px)' },
  normal: { value: 8, label: 'Normal (8px)' },
  relaxed: { value: 16, label: 'Relaxed (16px)' },
  loose: { value: 24, label: 'Loose (24px)' },
  spacious: { value: 32, label: 'Spacious (32px)' },
};

// Future snap zone definitions (for drag-and-drop)
export const SNAP_ZONES = {
  containerBounds: { enabled: true, threshold: 8 },
  centerLines: { enabled: true, threshold: 4 },
  elementEdges: { enabled: true, threshold: 6 },
  gridIncrement: { enabled: true, size: 8, threshold: 4 },
};

// Default position state
export const DEFAULT_POSITION = {
  align: 'center',        // 'left' | 'center' | 'right'
  vAlign: 'middle',       // 'top' | 'middle' | 'bottom'
  offsetX: 0,             // Horizontal offset in pixels
  offsetY: 0,             // Vertical offset in pixels
  spacing: 'normal',      // Spacing preset key
  marginTop: 0,
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
};

// Helper to get alignment styles
export const getAlignmentStyles = (position = {}) => {
  const { align = 'center', vAlign = 'middle', offsetX = 0, offsetY = 0 } = position;
  
  const styles = {
    display: 'flex',
    flexDirection: 'column',
  };
  
  // Horizontal alignment
  switch (align) {
    case 'left':
      styles.alignItems = 'flex-start';
      styles.textAlign = 'left';
      break;
    case 'right':
      styles.alignItems = 'flex-end';
      styles.textAlign = 'right';
      break;
    case 'center':
    default:
      styles.alignItems = 'center';
      styles.textAlign = 'center';
      break;
  }
  
  // Vertical alignment
  switch (vAlign) {
    case 'top':
      styles.justifyContent = 'flex-start';
      break;
    case 'bottom':
      styles.justifyContent = 'flex-end';
      break;
    case 'middle':
    default:
      styles.justifyContent = 'center';
      break;
  }
  
  // Offset transform
  if (offsetX !== 0 || offsetY !== 0) {
    styles.transform = `translate(${offsetX}px, ${offsetY}px)`;
  }
  
  return styles;
};

// PositionInspector Component
const PositionInspector = ({ 
  position = {},
  onUpdatePosition,
  elementLabel = 'Element',
  showAdvanced = false,
}) => {
  const currentPosition = { ...DEFAULT_POSITION, ...position };
  const [nudgeMode, setNudgeMode] = React.useState('normal');
  
  const handleUpdate = (key, value) => {
    onUpdatePosition?.({ ...currentPosition, [key]: value });
  };
  
  const handleNudge = (direction) => {
    const increment = NUDGE_INCREMENTS[nudgeMode];
    
    switch (direction) {
      case 'up':
        handleUpdate('offsetY', currentPosition.offsetY - increment);
        break;
      case 'down':
        handleUpdate('offsetY', currentPosition.offsetY + increment);
        break;
      case 'left':
        handleUpdate('offsetX', currentPosition.offsetX - increment);
        break;
      case 'right':
        handleUpdate('offsetX', currentPosition.offsetX + increment);
        break;
      default:
        break;
    }
  };
  
  const handleReset = () => {
    onUpdatePosition?.(DEFAULT_POSITION);
  };
  
  const hasCustomOffset = currentPosition.offsetX !== 0 || currentPosition.offsetY !== 0;

  return (
    <TooltipProvider>
      <div className="space-y-5">
        {/* Section Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Move className="w-4 h-4 text-orange-400" />
            <span className="text-sm font-medium text-white">Position</span>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="h-7 px-2 text-zinc-400 hover:text-white"
              >
                <RotateCcw className="w-3 h-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Reset to default</p>
            </TooltipContent>
          </Tooltip>
        </div>
        
        {/* Horizontal Alignment */}
        <div className="space-y-2">
          <Label className="text-zinc-400 text-xs uppercase tracking-wider">
            Horizontal Align
          </Label>
          <div className="flex gap-1 bg-zinc-800/50 p-1 rounded-lg">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => handleUpdate('align', 'left')}
                  className={`flex-1 flex items-center justify-center p-2 rounded-md transition-colors ${
                    currentPosition.align === 'left'
                      ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                  }`}
                >
                  <AlignLeft className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent><p>Align Left</p></TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => handleUpdate('align', 'center')}
                  className={`flex-1 flex items-center justify-center p-2 rounded-md transition-colors ${
                    currentPosition.align === 'center'
                      ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                  }`}
                >
                  <AlignCenter className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent><p>Align Center</p></TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => handleUpdate('align', 'right')}
                  className={`flex-1 flex items-center justify-center p-2 rounded-md transition-colors ${
                    currentPosition.align === 'right'
                      ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                  }`}
                >
                  <AlignRight className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent><p>Align Right</p></TooltipContent>
            </Tooltip>
          </div>
        </div>
        
        {/* Vertical Placement */}
        <div className="space-y-2">
          <Label className="text-zinc-400 text-xs uppercase tracking-wider">
            Vertical Place
          </Label>
          <div className="flex gap-1 bg-zinc-800/50 p-1 rounded-lg">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => handleUpdate('vAlign', 'top')}
                  className={`flex-1 flex items-center justify-center p-2 rounded-md transition-colors ${
                    currentPosition.vAlign === 'top'
                      ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                  }`}
                >
                  <AlignStartVertical className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent><p>Top</p></TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => handleUpdate('vAlign', 'middle')}
                  className={`flex-1 flex items-center justify-center p-2 rounded-md transition-colors ${
                    currentPosition.vAlign === 'middle'
                      ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                  }`}
                >
                  <AlignCenterVertical className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent><p>Middle</p></TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => handleUpdate('vAlign', 'bottom')}
                  className={`flex-1 flex items-center justify-center p-2 rounded-md transition-colors ${
                    currentPosition.vAlign === 'bottom'
                      ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                  }`}
                >
                  <AlignEndVertical className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent><p>Bottom</p></TooltipContent>
            </Tooltip>
          </div>
        </div>
        
        {/* Nudge Controls */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">
              Nudge
            </Label>
            <Select value={nudgeMode} onValueChange={setNudgeMode}>
              <SelectTrigger className="w-24 h-7 bg-zinc-800 border-zinc-700 text-white text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="fine" className="text-white text-xs">Fine (1px)</SelectItem>
                <SelectItem value="normal" className="text-white text-xs">Normal (4px)</SelectItem>
                <SelectItem value="coarse" className="text-white text-xs">Coarse (16px)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Nudge D-Pad */}
          <div className="flex justify-center">
            <div className="grid grid-cols-3 gap-1 w-fit">
              <div /> {/* Empty top-left */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleNudge('up')}
                    className="w-10 h-10 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 rounded-md text-zinc-400 hover:text-white transition-colors"
                  >
                    <ChevronUp className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent><p>Nudge Up ({NUDGE_INCREMENTS[nudgeMode]}px)</p></TooltipContent>
              </Tooltip>
              <div /> {/* Empty top-right */}
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleNudge('left')}
                    className="w-10 h-10 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 rounded-md text-zinc-400 hover:text-white transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent><p>Nudge Left ({NUDGE_INCREMENTS[nudgeMode]}px)</p></TooltipContent>
              </Tooltip>
              
              {/* Center indicator */}
              <div className="w-10 h-10 flex items-center justify-center">
                {hasCustomOffset ? (
                  <span className="text-[10px] text-zinc-500 font-mono">
                    {currentPosition.offsetX},{currentPosition.offsetY}
                  </span>
                ) : (
                  <Grid3X3 className="w-4 h-4 text-zinc-600" />
                )}
              </div>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleNudge('right')}
                    className="w-10 h-10 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 rounded-md text-zinc-400 hover:text-white transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent><p>Nudge Right ({NUDGE_INCREMENTS[nudgeMode]}px)</p></TooltipContent>
              </Tooltip>
              
              <div /> {/* Empty bottom-left */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleNudge('down')}
                    className="w-10 h-10 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 rounded-md text-zinc-400 hover:text-white transition-colors"
                  >
                    <ChevronDown className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent><p>Nudge Down ({NUDGE_INCREMENTS[nudgeMode]}px)</p></TooltipContent>
              </Tooltip>
              <div /> {/* Empty bottom-right */}
            </div>
          </div>
          
          {hasCustomOffset && (
            <p className="text-[10px] text-zinc-500 text-center">
              Offset: X={currentPosition.offsetX}px, Y={currentPosition.offsetY}px
            </p>
          )}
        </div>
        
        {/* Spacing Preset */}
        <div className="space-y-2">
          <Label className="text-zinc-400 text-xs uppercase tracking-wider">
            Spacing
          </Label>
          <Select 
            value={currentPosition.spacing} 
            onValueChange={(value) => handleUpdate('spacing', value)}
          >
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              {Object.entries(SPACING_PRESETS).map(([key, preset]) => (
                <SelectItem key={key} value={key} className="text-white">
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Advanced Margin Controls (optional) */}
        {showAdvanced && (
          <div className="space-y-3 pt-3 border-t border-zinc-800">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">
              Advanced Margins
            </Label>
            
            {['Top', 'Bottom', 'Left', 'Right'].map((dir) => {
              const key = `margin${dir}`;
              return (
                <div key={key} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-xs">{dir}</span>
                    <span className="text-zinc-500 text-xs font-mono">
                      {currentPosition[key] || 0}px
                    </span>
                  </div>
                  <Slider
                    value={[currentPosition[key] || 0]}
                    onValueChange={([value]) => handleUpdate(key, value)}
                    min={-50}
                    max={100}
                    step={1}
                    className="w-full"
                  />
                </div>
              );
            })}
          </div>
        )}
        
        {/* Future Drag-and-Drop Hint */}
        <div className="pt-2 border-t border-zinc-800">
          <p className="text-[10px] text-zinc-600 text-center">
            🔮 Guided snap positioning coming soon
          </p>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default PositionInspector;
